type ScrollTarget = Window | HTMLElement
type ReturnPoint = {
    owner: HTMLElement | Document
    target: ScrollTarget
    top: number
    left: number
    focus: HTMLElement | null
}

export interface DocumentEditorOwner {
    id: string
    historyEntry: string
    historyMode: 'route' | 'local'
    order: number
    parentId: string | null
    root: HTMLElement | null
    layout: 'page' | 'dialog'
    zIndex: number
    returns: ReturnPoint[]
}

let nextOrder = 0
const owners = new Map<string, DocumentEditorOwner>()
const backgroundInert = new Map<HTMLElement, boolean>()
const pendingReturns = new Set<DocumentEditorOwner>()

function orderedOwners() {
    return [...owners.values()].filter(owner => owner.root?.isConnected).sort((a, b) => a.order - b.order)
}

function isAvailable(element: HTMLElement) {
    if (!element.isConnected || element.closest('[hidden], [inert]')) return false
    for (let ancestor: HTMLElement | null = element; ancestor; ancestor = ancestor.parentElement) {
        if (getComputedStyle(ancestor).display === 'none' || getComputedStyle(ancestor).visibility === 'hidden') return false
    }
    return true
}

export function getActiveDocumentEditor(): HTMLElement | null {
    return typeof document === 'undefined' ? null : document.querySelector<HTMLElement>('[data-document-editor][data-editor-active="true"]')
}

/** Identity of the page owning window scroll, including a page below a desktop utility/dialog. */
export function getDocumentScrollOwner(): HTMLElement | Document {
    const pages = document.querySelectorAll<HTMLElement>('[data-document-editor][data-layout="page"]:not([hidden])')
    return pages.item(pages.length - 1) ?? document
}

/** Only for semantic step/tab navigation and restoration, never keyboard geometry. */
export function getEditorScrollOwner(element: Element | null): ScrollTarget | null {
    if (typeof window === 'undefined' || !(element instanceof HTMLElement) || !isAvailable(element)) return null
    const editor = element.closest<HTMLElement>('[data-document-editor]')
    if (!editor) return getActiveDocumentEditor() ? null : window
    if (editor.dataset.editorActive === 'false') return null
    return editor.dataset.layout === 'dialog' ? editor.querySelector<HTMLElement>('[data-editor-scroll]') : window
}

export function createDocumentEditorOwner(id: string, parentId: string | null, layout: DocumentEditorOwner['layout'], zIndex: number, historyEntry: string, historyMode: DocumentEditorOwner['historyMode'], openerPosition?: {x: number; y: number}): DocumentEditorOwner {
    // Intercepted routes mount in a sibling React slot, outside the opener's
    // context. They still belong above the currently active editor and must
    // return its focus/scroll position when closed.
    const parent = (parentId ? owners.get(parentId) : undefined) ?? orderedOwners().at(-1)
    const focus = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement : null
    const target = parent?.root?.dataset.layout === 'dialog'
        ? parent.root.querySelector<HTMLElement>('[data-editor-scroll]') ?? window : window
    // A new route can shrink the document before this layout effect runs.
    // Same-entry and local utilities still capture their immediate parent.
    const originalPosition = historyMode === 'route' && parent?.historyMode !== 'local' && parent?.historyEntry !== historyEntry
        ? openerPosition : undefined
    const point: ReturnPoint = {
        owner: parent?.root ?? getDocumentScrollOwner(), target, focus,
        top: originalPosition?.y ?? (target === window ? window.scrollY : (target as HTMLElement).scrollTop),
        left: originalPosition?.x ?? (target === window ? window.scrollX : (target as HTMLElement).scrollLeft),
    }
    return {id, historyEntry, historyMode, parentId: parent?.id ?? null, layout, zIndex, root: null, order: ++nextOrder, returns: [point, ...(parent?.returns ?? [])]}
}

/** A local utility is not recreated by history/reload; retain its route parent's position. */
export function getLocalEditorReturnPoint(): ReturnPoint | null {
    const active = orderedOwners().at(-1)
    if (!active) return null
    let routeOwner = active
    let parent = routeOwner.parentId ? owners.get(routeOwner.parentId) : undefined
    while (parent && (routeOwner.historyMode === 'local' || parent.historyEntry === routeOwner.historyEntry)) {
        routeOwner = parent
        parent = routeOwner.parentId ? owners.get(routeOwner.parentId) : undefined
    }
    if (routeOwner.historyMode === 'local' && !parent) return routeOwner.returns[0] ?? null
    return routeOwner === active ? null : active.returns.find(point => point.owner === routeOwner.root) ?? null
}

function synchronizeOwners() {
    const active = orderedOwners()
    const top = active.at(-1)
    for (const [index, owner] of active.entries()) {
        const root = owner.root!
        root.hidden = active.slice(index + 1).some(next => next.layout === 'page')
        root.inert = owner !== top
        root.toggleAttribute('inert', owner !== top)
        root.dataset.editorActive = String(owner === top)
        const parent = owner.parentId ? owners.get(owner.parentId) : undefined
        root.style.zIndex = String(Math.max(owner.zIndex, parent?.root ? Number(parent.root.style.zIndex) + 1 : 0))
    }
    if (top) {
        document.querySelectorAll<HTMLElement>('[data-editor-background]').forEach(background => {
            if (!backgroundInert.has(background)) backgroundInert.set(background, background.inert || background.hasAttribute('inert'))
            background.inert = true
            background.setAttribute('inert', '')
        })
    } else {
        backgroundInert.forEach((wasInert, background) => {
            background.inert = wasInert
            background.toggleAttribute('inert', wasInert)
        })
        backgroundInert.clear()
    }
}

export function registerDocumentEditor(owner: DocumentEditorOwner) {
    owners.set(owner.id, owner)
    synchronizeOwners()
}

export function unregisterDocumentEditor(owner: DocumentEditorOwner) {
    if (owners.get(owner.id) !== owner) return
    owners.delete(owner.id)
    synchronizeOwners()
    // React can detach several ancestors, replay Effects, or open a replacement
    // in one commit. Restore only after the actual surviving owner is known.
    pendingReturns.add(owner)
    if (pendingReturns.size > 1) return
    queueMicrotask(() => {
        const closed = [...pendingReturns].sort((a, b) => b.order - a.order)
        pendingReturns.clear()
        const current = getActiveDocumentEditor()
        for (const entry of closed) {
            if (owners.has(entry.id)) continue
            for (const point of entry.returns) {
                if (current ? point.owner !== current : point.owner !== document) continue
                const scrollTarget = current ? getEditorScrollOwner(current) : window
                const canRestoreScroll = point.target === scrollTarget &&
                    (point.target !== window || point.owner === getDocumentScrollOwner())
                const focus = point.focus && isAvailable(point.focus) ? point.focus
                    : current?.querySelector<HTMLElement>('[data-editor-header]')
                // A breakpoint can change the parent's scroll target while its
                // child is open. Return focus, letting the browser reveal it in
                // the new presentation instead of applying the old offset.
                focus?.focus({preventScroll: canRestoreScroll})
                if (!canRestoreScroll) return
                if (point.target === window) {
                    if (window.scrollY !== point.top || window.scrollX !== point.left) {
                        window.scrollTo({top: point.top, left: point.left, behavior: 'instant'})
                    }
                } else if ((point.target as HTMLElement).isConnected) {
                    const target = point.target as HTMLElement
                    if (target.scrollTop !== point.top || target.scrollLeft !== point.left) {
                        target.scrollTo({top: point.top, left: point.left, behavior: 'instant'})
                    }
                }
                return
            }
        }
    })
}
